import type { ReservationStatus } from '../api-client.ts';

/**
 * SSOT — reservation status → allowed UI actions.
 *
 * Derived from the backend domain state machine:
 *   modules/booking_core/domain/entities/reservation.ts → ALLOWED_TRANSITIONS
 *
 *   pending    → [confirmed, canceled]           — allocate, cancel
 *   confirmed  → [checked_in, canceled, no_show] — checkIn, cancel, noShow
 *   checked_in → [completed]                     — complete
 *   completed / canceled / no_show               — terminal
 *
 * Every page that renders action buttons (Dashboard cards,
 * ReservationsPage table, Detail drawer) MUST read from these helpers
 * so a button never appears for a transition the domain rejects. If
 * you see "illegal status transition" or "only confirmed reservations
 * can check in" in prod, the UI called a button that should not have
 * rendered — fix the gating here, not by weakening domain rules.
 *
 * Contract-tested against the backend state machine in
 * `tests/contract/reservation-actions.contract.test.ts`.
 */

export function canAllocate(status: ReservationStatus): boolean {
  return status === 'pending';
}

export function canCheckIn(status: ReservationStatus): boolean {
  return status === 'confirmed';
}

export function canComplete(status: ReservationStatus): boolean {
  return status === 'checked_in';
}

export function canMarkNoShow(status: ReservationStatus): boolean {
  return status === 'confirmed';
}

export function canCancel(status: ReservationStatus): boolean {
  return status === 'pending' || status === 'confirmed';
}

export function isTerminal(status: ReservationStatus): boolean {
  return status === 'completed' || status === 'canceled' || status === 'no_show';
}
